import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { Telemetry } from '~src/commonInfra/services/Telemetry';
import { TelemetryEvents, VPCFeatureUsageEvent, VPCFeatureUsageType } from '~src/commonInfra/telemetryEvents/TelemetryEvents';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { getSessionManagementServiceInstance } from '~src/sessionManagement/SessionManagementService';
import { isProjectSpace, isSiteSpace, SpaceInfo } from '~src/spacesPlatform/state/SpaceInfo';

import { DestinationProjectContentType, DestinationProjects } from '../resources/DestinationProjects';
import { IDestinationProject } from '../ServerApi/types/IDestinationProject';

export const LocalStorageKeyPrefix = 'ProjectTreeToggleState';
export type ToggleState = 'on' | 'off';
export const projectTreeToggleFeatureName = 'ProjectTreeToggle';
export const projectTreeFeatureName = 'ProjectTree';

export class ProjectTreeService {
    public static instance = new ProjectTreeService();

    private treeNodeExpandedValues: Set<string>;

    public toggleState: ToggleState = 'off';

    public initialize() {
        const state = localStorage.getItem(this.getToggleStateLocalStorageKey()) as ToggleState;
        this.toggleState = state || 'on';
        this.triggerOnProjectTreeToggleStateChanged();
    }

    public onProjectTreeToggleStateChanged: ((toggleState: ToggleState) => void)[] = [];

    public async getAllProjects(): Promise<IDestinationProject[]> {
        const contentType = 'project' as DestinationProjectContentType;
        const projectsQuery = DestinationProjects.getQueryForSpecificSourceContentAndMatchText(contentType, [], '');
        const result = await SliceableHelper.fetchAllConcurrent(projectsQuery);
        return result.items;
    }

    public isProjectTreeEnabled(spaceInfo: SpaceInfo) {
        return FeatureFlags.projectTreeEnabled && (isSiteSpace(spaceInfo) || isProjectSpace(spaceInfo));
    }

    public toggle() {
        this.toggleState = this.toggleState === 'on' ? 'off' : 'on';
        localStorage.setItem(this.getToggleStateLocalStorageKey(), this.toggleState);
        this.triggerOnProjectTreeToggleStateChanged();

        this.sendProjectTreeToggleUsageTelemetryEvent('click_through');
    }

    public sendProjectTreeToggleUsageTelemetryEvent(usageType: VPCFeatureUsageType) {
        Telemetry.sendEvent<VPCFeatureUsageEvent>({
            type: TelemetryEvents.VPCFeatureUsage,
            feature_name: projectTreeToggleFeatureName,
            usage_type: usageType,
            extra_parameter_a: this.toggleState
        });
    }

    public sendProjectTreeUsageTelemetryEvent(usageType: VPCFeatureUsageType) {
        Telemetry.sendEvent<VPCFeatureUsageEvent>({
            type: TelemetryEvents.VPCFeatureUsage,
            feature_name: projectTreeFeatureName,
            usage_type: usageType
        });
    }

    public saveTreeNodeExpandedValues(values: Set<string>) {
        this.treeNodeExpandedValues = new Set(values);
    }

    public getTreeNodeExpandedValues(): Set<string> {
        return this.treeNodeExpandedValues;
    }

    private triggerOnProjectTreeToggleStateChanged() {
        this.onProjectTreeToggleStateChanged.forEach(handler => {
            handler(this.toggleState);
        });
    }

    private getToggleStateLocalStorageKey() {
        const sessionManagementService = getSessionManagementServiceInstance();
        return LocalStorageKeyPrefix + '-' + sessionManagementService.sessionUserLuid();
    }
}